import { KeyValue } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AFAddressHistoryDomain, AFAuthorization, AFEntity, AFNavigation, AFPersonalInformation, AFRegistrationDomain, SignInModel, AFSummaryOfRights, AFIntroduction, AFDisputeQuestionPojo, AFLocationConfirmation } from '../model/af.model';
import moment from 'moment';
import * as bcrypt from 'bcryptjs';
import { FormGroup } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class AuthService {


  constructor(private http: HttpClient) {
  }

  getIntroduction(): Observable<AFIntroduction> {
    return this.http.post<AFIntroduction>(environment.api_uri + 'getIntroduction', { 'verifyId': this.getVerifyId() });
  }

  getApplicantDetails(isNhp : boolean): Observable<AFEntity> {
    return this.http.post<AFEntity>(environment.api_uri + 'getApplicant', { 'verifyId': this.getVerifyId() , 'isNhp':isNhp});
  }

  getVendorDetails(): Observable<AFRegistrationDomain> {
    return this.http.post<AFRegistrationDomain>(environment.api_uri + 'registrationVendor', { 'verifyId': this.getVerifyId() });
  }

  getregistrationDetails(): Observable<AFRegistrationDomain> {
    return this.http.post<AFRegistrationDomain>(environment.api_uri + 'registration', { 'verifyId': this.getVerifyId() });
  }

  getPersonalInformation(): Observable<AFPersonalInformation> {
    return this.http.post<AFPersonalInformation>(environment.api_uri + 'getPersonalInformation', { 'verifyId': this.getVerifyId() })
      .pipe(
        map(response => {
          if (response.prevBankFromDate) {
            response.prevBankFromDate = new Date(response.prevBankFromDate);
          }
          if (response.prevBankEndDate) {
            response.prevBankEndDate = new Date(response.prevBankEndDate);
          }
          response.otherNameList.forEach(item => {
            if (item.dateNameChange) {
              item.dateNameChange = new Date(item.dateNameChange);
            }

          });
          return response;
        }));
  }

  getAddressHistory(): Observable<AFAddressHistoryDomain> {
    return this.http.post<AFAddressHistoryDomain>(environment.api_uri + 'getAddressHistory', { 'verifyId': this.getVerifyId() })
      .pipe(
        map(response => {

          response.addressList.forEach(item => {
            if (item.startDate) {
              if (item.startDate) {
                item.startDate = new Date(item.startDate);
              }
              if (item.endDate) {
                item.endDate = new Date(item.endDate);
              }
            }

            if (item.usState) {
              item.usState = item.usState + "";
            }

          });
          return response;
        }));
  }

  getLocationConfirmation(): Observable<AFLocationConfirmation> {
    return this.http.post<AFLocationConfirmation>(environment.api_uri + 'getLocationConfirmation', { 'verifyId': this.getVerifyId() })
      .pipe(
        map(response => {
          if (response.changedAddress && response.changedAddress.usState) {
            response.changedAddress.usState = response.changedAddress.usState + "";
          }
          return response;
        }));
  }

  getAuthorizationDetails(): Observable<AFAuthorization> {
    return this.http.post<AFAuthorization>(environment.api_uri + 'getAuthorization', { 'verifyId': this.getVerifyId() });
  }

  getViewAAGeneratedLetter(): Observable<string> {
    return this.http.post<string>(environment.api_uri + 'viewAAGeneratedLetter', { 'tokenId': this.getAaToken() });
  }

  validateNHPToken(token: string, verifyId : string) {
    return this.http.post<string>(environment.api_uri + 'validateNHPToken', { 'token': token,'verifyId' : verifyId });
  }

  getDisputeRegistration(): Observable<string> {
    return this.http.post<string>(environment.api_uri + 'disputeRegistration', { 'verifyId': this.getVerifyId() });
  }

  getDisputeQuestionnaire(): Observable<AFDisputeQuestionPojo> {
    return this.http.post<AFDisputeQuestionPojo>(environment.api_uri + 'getDisputeQuestionnaire', { 'verifyId': this.getVerifyId() });
  }

  saveDisputeQuestionnaire(afDisputeQuestionPojo): Observable<AFDisputeQuestionPojo> {
    const requestUrl = environment.api_uri + 'saveQuestionsAnswers';
    return this.http.post<AFDisputeQuestionPojo>(requestUrl, afDisputeQuestionPojo);
  }

  submitDisputeQuestionnaire(afDisputeQuestionPojo): Observable<string> {
    const requestUrl = environment.api_uri + 'submitQuestionsAnswers';
    return this.http.post<string>(requestUrl, afDisputeQuestionPojo);
  }

  saveAuthorization(afAuthorization): Observable<AFAuthorization> {
    const requestUrl = environment.api_uri + 'saveAuthorization';
    return this.http.post<AFAuthorization>(requestUrl, afAuthorization);
  }


  submitLocationConfirmation(AFLocationConfirmation): Observable<AFLocationConfirmation> {
    const requestUrl = environment.api_uri + 'saveLocationConfirmation';
    return this.http.post<AFLocationConfirmation>(requestUrl, AFLocationConfirmation);

  }

  removeAttachment(attachmentId: string): Observable<string> {
    const requestUrl = environment.api_uri + 'removeAsiAttachment';
    const fd = new FormData();
    fd.append("attachmentId", attachmentId);
    return this.http.post<string>(requestUrl, fd);
  }

  removeMongoAttachment(attachmentId: string): Observable<string> {
    const requestUrl = environment.api_uri + 'removeAttachment';
    const fd = new FormData();
    fd.append("attachmentId", attachmentId);
    return this.http.post<string>(requestUrl, fd);
  }

  removeMongoAttachmentPromise(attachmentId: string) {
    const requestUrl = environment.api_uri + 'removeAttachment';
    const fd = new FormData();
    fd.append("attachmentId", attachmentId);
    return firstValueFrom(this.http.post<string>(requestUrl, fd));
  }

  getSummaryLanguage(): Observable<AFSummaryOfRights> {
    return this.http.post<AFSummaryOfRights>(environment.api_uri + 'summaryOfRights', { 'verifyId': this.getLocalVerifyId() });
  }

  forgotPassword(securityQuestionDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'forgotPassword';
    return this.http.post<AFRegistrationDomain>(requestUrl, securityQuestionDomain);
  }

  forgotPasswordOTP(): Observable<SignInModel> {
    return this.http.post<SignInModel>(environment.api_uri + 'forgotPasswordOTP', { 'verifyId': this.getVerifyId() });
  }

  signIn(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'signIn';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  validateOTP(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'validateOTP';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  validateSecurityQuestions(securityQuestionDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'validateSecurityQuestions';
    return this.http.post<AFRegistrationDomain>(requestUrl, securityQuestionDomain);
  }

  validateVendorSecurityQuestions(securityQuestionDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'validateVendorSecurityQuestions';
    return this.http.post<AFRegistrationDomain>(requestUrl, securityQuestionDomain);
  }

  resetPassword(resetPasswordDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'resetPassword';
    return this.http.post<AFRegistrationDomain>(requestUrl, resetPasswordDomain);
  }

  resetVendorPassword(resetPasswordDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'resetVendorPassword';
    return this.http.post<AFRegistrationDomain>(requestUrl, resetPasswordDomain);
  }

  createProfile(RegistrationDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'createProfile';
    return this.http.post<AFRegistrationDomain>(requestUrl, RegistrationDomain);
  }

  submitPersonalInformation(AFPersonalInformation: AFPersonalInformation): Observable<AFPersonalInformation> {
    const requestUrl = environment.api_uri + 'savePersonalInformation';

    if (AFPersonalInformation.prevBankFromDate) {
      AFPersonalInformation.prevBankFromDate = moment(AFPersonalInformation.prevBankFromDate).format('MM/DD/YYYY');
    }
    if (AFPersonalInformation.prevBankEndDate) {
      AFPersonalInformation.prevBankEndDate = moment(AFPersonalInformation.prevBankEndDate).format('MM/DD/YYYY');
    }
    AFPersonalInformation.otherNameList.forEach(item => {
      if (item.dateNameChange) {
        item.dateNameChange = moment(item.dateNameChange).format('MM/DD/YYYY');
      }
    });

    if (AFPersonalInformation.prevBank !== '1') {
      AFPersonalInformation.prevBankFromDate = '';
      AFPersonalInformation.prevBankEndDate = '';
      AFPersonalInformation.personNumber = '';
      AFPersonalInformation.bankAffiliate = '';
    }

    if (AFPersonalInformation.usGreenCard !== '1') {
      AFPersonalInformation.usGreenCardDocumentNumber = '';
      AFPersonalInformation.usGreenCardIssueMonth = '';
      AFPersonalInformation.usGreenCardIssueDay = '';
      AFPersonalInformation.usGreenCardIssueYear = '';
      AFPersonalInformation.usGreenCardExpiryMonth = '';
      AFPersonalInformation.usGreenCardExpiryDay = '';
      AFPersonalInformation.usGreenCardExpiryYear = '';
      AFPersonalInformation.usGreenCardSSN = '';
    }

    if (AFPersonalInformation.confirmSsn) {
      let salt = bcrypt.genSaltSync();
      AFPersonalInformation.confirmSsn = bcrypt.hashSync(AFPersonalInformation.confirmSsn, salt);
    }

    return this.http.post<AFPersonalInformation>(requestUrl, AFPersonalInformation);
  }

  submitAddressHistory(AFAddressHistoryDomain): Observable<AFAddressHistoryDomain> {
    const requestUrl = environment.api_uri + 'saveAddress';
    return this.http.post<AFAddressHistoryDomain>(requestUrl, AFAddressHistoryDomain);
  }


  updatePassword(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'updatePassword';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  updateVendorPassword(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'updateVendorPassword';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  isAuthenticated(): boolean {
    return (sessionStorage.getItem('verifyId') != null);
  }

  hasValidatedReset(): boolean {
    return (sessionStorage.getItem('maxCredential') != null);
  }

  validNavigationalUrl(navUrl: string): boolean {
    let userNavItems: AFNavigation[] = this.getNavLinks();
    let maxNavigationValue: string = sessionStorage.getItem('maxNavigation');
    let returnVal: boolean = true;
    if (userNavItems) {
      for (let entry of userNavItems) {
        if (entry.routerLink == '/' + navUrl) {
          if (Number(maxNavigationValue) < entry.id) {
            returnVal = false;
          }
          break;
        }
      }
    }
    return returnVal;
  }

  getVerifyId(): string {
    return (sessionStorage.getItem('verifyId'));
  }

  getLocalVerifyId(): string { //session variables are tied to browser tabs
    return (localStorage.getItem('verifyIdLocal'));
  }

  getAaToken(): string {
    return (sessionStorage.getItem('aaToken'));
  }

  getBookmarkUrl(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.bookmarkUrl;
    }
    return null;
  }

  getUser(): AFEntity {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u;
    }
    return null;
  }

  getNavLinks(): AFNavigation[] {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      let showCpa = sessionStorage.getItem('show_cpa');
      if (this.getJobState() && this.getJobState().toUpperCase() == 'CA' || showCpa == "true") {
        return u.navList;
      }

      return u.navList.filter((value, key) => {
        return (value.routerLink != "/cpa");
      });
    }
    return null;
  }

  getAfId(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.afId;
    }
    return null;
  }

  getJobState(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.jobState;
    }
    return null;
  }

  getJobCountry(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.jobCountry;
    }
    return null;
  }

  getJobSubCountry(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.jobSubCountry;
      }
    }
    return null;
  }

  getJobCountryValue(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.jobCountryValue;
      }

    }
    return null;
  }

  getAddressLimit(): number {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.addressLimit;
      }
    }
    return null;
  }

  getJobCountryOnboardingList(): boolean {
    // add new country to onboard as part of OUS list 
    // then it will return ture to action accordingly
    const listOfOUS = ['AU', 'IRL'];

    let isOUSList = false;
    let currentJobCountry = this.getJobCountry();
    listOfOUS.forEach((country) => {
      if (currentJobCountry == country) {
        isOUSList = true;
      }
    });

    return isOUSList;
  }


  getVendorUser(): AFRegistrationDomain {
    const user = sessionStorage.getItem(environment.user_vendor_object);
    if (user) {
      const u: AFRegistrationDomain = JSON.parse(user);
      return u;
    }
    return null;
  }

  isSwapsAP(): boolean {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      return u.isSwapsAP;
    }
    return null;
  }

  getOtherNameText(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.otherNameText;
      }
    }
    return null;
  }

  getEmploymentGapInstruction(): number {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.employmentGapInstruction;
      }
    }
    return null;
  }

  getAddressHistoryInstruction(): number {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.addressHistoryInstruction;
      }
    }
    return null;
  }

  getOutsideJobCountryDocument(): number {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.outsideJobCountryDocument;
      }
    }
    return null;
  }

  getRequireOutsideJobCountryDocument(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules && u.countryRules.requiredDocumentJobCountry) {
        return u.countryRules.requiredDocumentJobCountry;
      }
    }
    return null;
  }

  getEducationHistoryInstruction(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.educationHistoryInstruction;
      }
    }
    return null;
  }

  getLicenseIdText(): string {
    const user = sessionStorage.getItem(environment.user_object);
    if (user) {
      const u: AFEntity = JSON.parse(user);
      if (u.countryRules) {
        return u.countryRules.licenseIdText;
      }
    }
    return null;
  }

  createVendorProfile(RegistrationDomain): Observable<AFRegistrationDomain> {
    const requestUrl = environment.api_uri + 'createVendorProfile';
    return this.http.post<AFRegistrationDomain>(requestUrl, RegistrationDomain);
  }

  vendorSignIn(SignInModel): Observable<SignInModel> {
    const requestUrl = environment.api_uri + 'vendorSignIn';
    return this.http.post<SignInModel>(requestUrl, SignInModel);
  }

  generatePDF(afId: string, verifyId: string, fileName: string): Observable<any> {
    let params = new HttpParams().set("afId", afId).set("verifyId", verifyId);
    let headers = new HttpHeaders({
      "Accept": "application/pdf"
    });

    return this.http.post(environment.api_uri + "generatePDF", params, { headers: headers, responseType: "blob" }).pipe(
      tap((response: Blob) => {
        const blob = new Blob([response], {
          type: "application/pdf"
        });

        // Browsers require downloads from an <a> for security purposes, generating a 
        // temporary link to download directly to the browser in this way is supported by all browsers. 
        // Not all browsers currently support APIs that handle this.
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }),
      catchError(error => {
        console.log("Error downloading file: ", error);
        return throwError(() => error);
      }));
  }

  setFocus(form: FormGroup){
    if(form.invalid){
      // Get the first invalid formControlName string to set focus: 
      const firstInvalidField = Object.keys(form.controls).find(field => form.get(field)?.invalid);
      // To select the respective form DOM element string
      const selector = `[formControlName=${firstInvalidField}]`;
      if(firstInvalidField && document.querySelector(selector)?.childNodes?.length){
        const formEle = document.querySelector(selector).querySelectorAll('input, textarea, select');
        if(formEle && formEle.length){
          (<HTMLElement>formEle[0]).focus();
        } else {
          const focusElement = document.querySelector(selector).querySelector('div[tabindex]');
          if(focusElement){
            (<HTMLElement>focusElement).focus();
          } else {
            (<HTMLElement>document.querySelector(selector)).focus();
          }
        }
      } else {
        (<HTMLElement>document.querySelector(selector)).focus();
      }
      setTimeout(()=>{
        window.scrollTo(0, window.scrollY);
      }, 100);
    }
  }

}

export function valueAscOrder(a: KeyValue<number, string>, b: KeyValue<number, string>): number {
  return a.value.localeCompare(b.value);
}
